import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useState, type FormEvent } from "react";
import { valorPorExtenso } from "../../../shared/extenso";
import type { ItemBusca, ResumoOrcamento, SecaoCalculada, TipoComposicao } from "../../../shared/tipos";
import { api } from "../api/cliente";
import { chaves } from "../api/consultas";
import { useAvisos } from "./Avisos";
import { useTermoComAtraso } from "./useTermoComAtraso";
import { formatarMoeda, formatarQuantidade, lerNumero, semAutocompletar, Tabela } from "./ui";

/** Invalida o orçamento (e listas que mostram contagem) depois de uma alteração. */
export function useAtualizarOrcamento(orcamentoId: number) {
  const queryClient = useQueryClient();
  return () => {
    queryClient.invalidateQueries({ queryKey: chaves.orcamento(orcamentoId) });
    queryClient.invalidateQueries({ queryKey: chaves.painel });
  };
}

export function useAdicionarItem(orcamentoId: number) {
  const atualizar = useAtualizarOrcamento(orcamentoId);
  const { avisarErro } = useAvisos();
  return useMutation({
    mutationFn: (dados: { item_id: number; quantidade: number; secao_id?: number | null }) =>
      api(`/api/orcamentos/${orcamentoId}/itens`, { method: "POST", body: dados }),
    onSuccess: atualizar,
    onError: avisarErro,
  });
}

// ----------------------------------------------------------------- busca de itens
function LinhaBusca({ item, orcamentoId, secaoId, onAdicionado }: { item: ItemBusca; orcamentoId: number; secaoId: number | null; onAdicionado: () => void }) {
  const [qtd, setQtd] = useState("1");
  const [mult, setMult] = useState("1");
  const [palavras, setPalavras] = useState(item.palavras_chave ?? "");
  const adicionar = useAdicionarItem(orcamentoId);
  const { avisarErro } = useAvisos();
  const quantidadeFinal = lerNumero(qtd) * lerNumero(mult);

  return (
    <tr>
      <td>{item.codigo}</td>
      <td>{item.descricao}</td>
      <td>{item.unidade || ""}</td>
      <td className="num">
        <input type="text" value={qtd} style={{ width: 55 }} onChange={(e) => setQtd(e.target.value)} {...semAutocompletar} />
      </td>
      <td className="num">
        <input type="text" value={mult} style={{ width: 55 }} onChange={(e) => setMult(e.target.value)} {...semAutocompletar} />
      </td>
      <td className="num">{formatarMoeda(item.preco_unitario)}</td>
      <td className="num">{formatarMoeda(quantidadeFinal * item.preco_unitario)}</td>
      <td>
        <input
          type="text"
          value={palavras}
          placeholder="sinônimos..."
          style={{ width: 150 }}
          {...semAutocompletar}
          onChange={(e) => setPalavras(e.target.value)}
          onBlur={async () => {
            if (palavras === (item.palavras_chave ?? "")) return;
            try {
              await api(`/api/itens/${item.id}/palavras-chave`, { method: "PATCH", body: { palavras_chave: palavras } });
              item.palavras_chave = palavras;
            } catch (erro) {
              avisarErro(erro);
            }
          }}
        />
      </td>
      <td>
        <button
          type="button"
          disabled={adicionar.isPending}
          onClick={() => adicionar.mutate({ item_id: item.id, quantidade: quantidadeFinal, secao_id: secaoId }, { onSuccess: onAdicionado })}
        >
          + Add
        </button>
      </td>
    </tr>
  );
}

/** Itens mostrados por página na busca dentro do orçamento. */
const ITENS_POR_PAGINA = 10;

export function BuscaItensOrcamento({
  orcamentoId,
  secoes = [],
  secaoAtiva = null,
  onSecaoAtiva,
}: {
  orcamentoId: number;
  secoes?: SecaoCalculada[];
  secaoAtiva?: number | null;
  onSecaoAtiva?: (id: number | null) => void;
}) {
  const [q, setQ] = useState("");
  const [palavraChave, setPalavraChave] = useState("");
  const termo = useTermoComAtraso([q, palavraChave].filter(Boolean).join(" "));
  const [paginaDoTermo, setPaginaDoTermo] = useState({ termo, pagina: 1 });
  // Nova busca volta para a primeira página.
  const pagina = paginaDoTermo.termo === termo ? paginaDoTermo.pagina : 1;
  const irParaPagina = (p: number) => setPaginaDoTermo({ termo, pagina: p });
  const { avisar } = useAvisos();

  const { data: resultados = [], isFetching } = useQuery({
    queryKey: ["itens-orcamento", orcamentoId, termo, pagina],
    queryFn: () =>
      api<ItemBusca[]>(
        `/api/itens?${new URLSearchParams({ q: termo, orcamento_id: String(orcamentoId), pagina: String(pagina), por_pagina: String(ITENS_POR_PAGINA) })}`,
      ),
    placeholderData: (anterior) => anterior,
  });
  const total = resultados[0]?.total_resultados ?? resultados.length;

  return (
    <div className="card no-print" id="busca-itens">
      {secoes.length > 0 && (
        <div className="form-row">
          <label>
            Adicionar em
            <select value={secaoAtiva ?? ""} onChange={(e) => onSecaoAtiva?.(e.target.value ? Number(e.target.value) : null)}>
              <option value="">Sem seção</option>
              {secoes.map((sec) => (
                <option key={sec.id} value={sec.id}>
                  {sec.numero} {sec.titulo}
                </option>
              ))}
            </select>
          </label>
        </div>
      )}
      <div className="form-row">
        <label>
          Localizar
          <input type="search" placeholder="código ou descrição..." style={{ minWidth: 280 }} value={q} onChange={(e) => setQ(e.target.value)} {...semAutocompletar} />
        </label>
        <label>
          Palavra-chave
          <input type="text" placeholder="ex: manilha, tubulação..." style={{ minWidth: 200 }} value={palavraChave} onChange={(e) => setPalavraChave(e.target.value)} {...semAutocompletar} />
        </label>
      </div>
      <p className="note">
        A busca já usa a família e o sindicato deste orçamento — cada código aparece uma única vez, com o preço correto para
        esta obra. Não achou pelo nome oficial? Digite outro termo na "Palavra-chave" — a busca passa a valer pra esse item
        também, mesmo que a palavra não esteja na descrição da EMBASA.
      </p>
      <Tabela>
        <thead>
          <tr>
            <th>Código</th>
            <th>Descrição</th>
            <th>Un.</th>
            <th className="num">Qtd.</th>
            <th className="num">Multiplicador</th>
            <th className="num">Preço</th>
            <th className="num">Total</th>
            <th>Palavra-chave</th>
            <th />
          </tr>
        </thead>
        <tbody>
          {resultados.map((r, i) => (
            <LinhaBusca
              key={`${r.id}-${i}`}
              item={r}
              orcamentoId={orcamentoId}
              secaoId={secaoAtiva}
              onAdicionado={() =>
                avisar(
                  `${r.codigo} adicionado${secaoAtiva ? ` em ${secoes.find((sec) => sec.id === secaoAtiva)?.numero ?? ""}` : ""}.`,
                )
              }
            />
          ))}
        </tbody>
      </Tabela>
      <Paginacao pagina={pagina} total={total} porPagina={ITENS_POR_PAGINA} carregando={isFetching} onMudar={irParaPagina} />
    </div>
  );
}

function Paginacao({
  pagina,
  total,
  porPagina,
  carregando,
  onMudar,
}: {
  pagina: number;
  total: number;
  porPagina: number;
  carregando: boolean;
  onMudar: (pagina: number) => void;
}) {
  const paginas = Math.max(1, Math.ceil(total / porPagina));
  const inicio = total ? (pagina - 1) * porPagina + 1 : 0;
  const fim = Math.min(pagina * porPagina, total);

  return (
    <div className="paginacao">
      <span className="note">
        {carregando ? "Buscando…" : `Mostrando ${inicio}–${fim} de ${total} ite${total === 1 ? "m" : "ns"}`}
      </span>
      {paginas > 1 && (
        <div className="paginacao-botoes">
          <button type="button" className="btn-ghost" disabled={pagina <= 1} onClick={() => onMudar(1)} title="Primeira página">
            «
          </button>
          <button type="button" className="btn-ghost" disabled={pagina <= 1} onClick={() => onMudar(pagina - 1)}>
            ‹ {porPagina} anteriores
          </button>
          <label className="paginacao-pagina">
            Página
            <select value={pagina} onChange={(e) => onMudar(Number(e.target.value))}>
              {Array.from({ length: paginas }, (_, i) => (
                <option key={i + 1} value={i + 1}>
                  {i + 1}
                </option>
              ))}
            </select>
            de {paginas}
          </label>
          <button type="button" className="btn-ghost" disabled={pagina >= paginas} onClick={() => onMudar(pagina + 1)}>
            Próximos {porPagina} ›
          </button>
          <button type="button" className="btn-ghost" disabled={pagina >= paginas} onClick={() => onMudar(paginas)} title="Última página">
            »
          </button>
        </div>
      )}
    </div>
  );
}

// ----------------------------------------------------------------- composições especiais
export const BLOCOS_COMPOSICAO: { tipo: TipoComposicao; rotulo: string; dica: string }[] = [
  {
    tipo: "mobilizacao",
    rotulo: "2. Mobilização",
    dica:
      "Códigos 01.01.00-07 / 01.91.00-01 (SER.CG, preço 0,00 na tabela — depende do dimensionamento do canteiro). Escopo oficial: Caderno de Encargos, item 5.1.2.1 — diligenciamento/transporte/montagem de equipamentos, instalações provisórias e infraestrutura do canteiro.",
  },
  {
    tipo: "desmobilizacao",
    rotulo: "3. Desmobilização",
    dica:
      "Mesmos códigos de canteiro, etapa de desmobilização. Escopo oficial: Caderno de Encargos, item 5.1.2.1 — retirada de instalações, retorno de equipamentos, recomposição da área.",
  },
  {
    tipo: "administracao_local",
    rotulo: "4. Administração Local",
    dica:
      "Código 01.99.01 e correlatos. Escopo oficial (Caderno de Encargos, item 5.1.2.1): engenharia, administração de pessoal, suprimento, segurança do trabalho, vigilância, transporte, comunicação, higiene, saúde, alimentação e assistência social do canteiro.",
  },
];

function CampoAutocompletar({
  rotulo,
  valor,
  onChange,
  onEscolher,
  orcamentoId,
  ...props
}: {
  rotulo: string;
  valor: string;
  onChange: (v: string) => void;
  onEscolher: (item: ItemBusca) => void;
  orcamentoId: number;
  placeholder?: string;
  required?: boolean;
  style?: React.CSSProperties;
}) {
  const [aberto, setAberto] = useState(false);
  const termo = useTermoComAtraso(valor);
  const { data: opcoes = [] } = useQuery({
    queryKey: ["buscar-embasa", orcamentoId, termo],
    queryFn: () => api<ItemBusca[]>(`/api/modelos/buscar-embasa?${new URLSearchParams({ q: termo, orcamento_id: String(orcamentoId) })}`),
    enabled: aberto && termo.length >= 2,
  });

  return (
    <label style={{ position: "relative" }}>
      {rotulo}
      <input
        type="text"
        value={valor}
        {...props}
        {...semAutocompletar}
        onChange={(e) => {
          onChange(e.target.value);
          setAberto(true);
        }}
        onFocus={() => setAberto(true)}
        onBlur={() => setTimeout(() => setAberto(false), 150)}
      />
      <div className={`ac-list${aberto && termo.length >= 2 && opcoes.length ? " show" : ""}`}>
        {opcoes.slice(0, 25).map((r) => (
          <div
            key={r.id}
            className="ac-item"
            onMouseDown={(ev) => {
              ev.preventDefault();
              onEscolher(r);
              setAberto(false);
            }}
          >
            <span className="ac-codigo-txt">{r.codigo}</span>
            {r.descricao}
            <span className="ac-preco-txt">
              R$ {formatarMoeda(r.preco_unitario)} / {r.unidade || ""}
            </span>
          </div>
        ))}
      </div>
    </label>
  );
}

export function FormComposicao({ orcamentoId, tipo }: { orcamentoId: number; tipo: TipoComposicao }) {
  const vazio = { descricao: "", codigo_referencia: "", unidade: "", quantidade: "1", multiplicador: "1", valor_unitario: "0" };
  const [form, setForm] = useState(vazio);
  const atualizar = useAtualizarOrcamento(orcamentoId);
  const { avisarErro } = useAvisos();

  const qtd = lerNumero(form.quantidade);
  const mult = lerNumero(form.multiplicador, 1) || 1;
  const valor = lerNumero(form.valor_unitario);

  const adicionar = useMutation({
    mutationFn: () =>
      api(`/api/orcamentos/${orcamentoId}/composicao`, {
        method: "POST",
        body: {
          tipo,
          descricao: form.descricao,
          codigo_referencia: form.codigo_referencia,
          unidade: form.unidade,
          quantidade: qtd * mult,
          valor_unitario: valor,
        },
      }),
    onSuccess: () => {
      setForm(vazio);
      atualizar();
    },
    onError: avisarErro,
  });

  const escolher = (r: ItemBusca) =>
    setForm((f) => ({ ...f, descricao: r.descricao, codigo_referencia: r.codigo, unidade: r.unidade || "", valor_unitario: String(r.preco_unitario) }));

  return (
    <>
      <form
        className="form-row no-print"
        autoComplete="off"
        onSubmit={(ev: FormEvent) => {
          ev.preventDefault();
          adicionar.mutate();
        }}
      >
        <CampoAutocompletar
          rotulo="Descrição"
          valor={form.descricao}
          onChange={(v) => setForm((f) => ({ ...f, descricao: v }))}
          onEscolher={escolher}
          orcamentoId={orcamentoId}
          required
          placeholder="ex: Engenheiro Residente"
          style={{ minWidth: 220 }}
        />
        <CampoAutocompletar
          rotulo="Código de referência"
          valor={form.codigo_referencia}
          onChange={(v) => setForm((f) => ({ ...f, codigo_referencia: v }))}
          onEscolher={escolher}
          orcamentoId={orcamentoId}
          placeholder="opcional ou digite p/ buscar"
        />
        <label>
          Unidade
          <input type="text" placeholder="MÊS, UN..." style={{ width: 90 }} value={form.unidade} onChange={(e) => setForm({ ...form, unidade: e.target.value })} {...semAutocompletar} />
        </label>
        <label>
          Qtd.
          <input type="text" style={{ width: 60 }} value={form.quantidade} onChange={(e) => setForm({ ...form, quantidade: e.target.value })} {...semAutocompletar} />
        </label>
        <label>
          Multiplicador
          <input type="text" style={{ width: 60 }} value={form.multiplicador} onChange={(e) => setForm({ ...form, multiplicador: e.target.value })} {...semAutocompletar} />
        </label>
        <label>
          Valor unitário (R$)
          <input type="text" style={{ width: 110 }} value={form.valor_unitario} onChange={(e) => setForm({ ...form, valor_unitario: e.target.value })} {...semAutocompletar} />
        </label>
        <span className="note" style={{ alignSelf: "end", marginBottom: 9 }}>
          = {formatarQuantidade(qtd)} × {formatarQuantidade(mult)} = {formatarQuantidade(qtd * mult)} un. · Total: R$ {formatarMoeda(qtd * mult * valor)}
        </span>
        <button type="submit" disabled={adicionar.isPending}>
          Adicionar
        </button>
      </form>
      <p className="note no-print">
        Digite na Descrição ou no Código ref. para buscar itens reais da tabela oficial já importada — ao escolher um, código,
        unidade e valor unitário são preenchidos automaticamente.
      </p>
    </>
  );
}

// ----------------------------------------------------------------- resumo financeiro
export function ResumoFinanceiro({ resumo }: { resumo: ResumoOrcamento }) {
  return (
    <div className="card">
      <div className="total-line">
        <span>Subtotal serviços (itens + mobilização/desmob./adm. local)</span>
        <span>R$ {formatarMoeda(resumo.subtotal_servicos_com_especiais)}</span>
      </div>
      <div className="total-line">
        <span>Subtotal materiais</span>
        <span>R$ {formatarMoeda(resumo.subtotal_materiais)}</span>
      </div>
      {resumo.bdi_servicos_pct !== null ? (
        <div className="total-line">
          <span>
            BDI Serviços ({resumo.bdi_servicos_pct}% — ISS {resumo.iss_pct}%, fonte {resumo.bdi_servicos_versao})
          </span>
          <span>R$ {formatarMoeda(resumo.valor_bdi_servicos)}</span>
        </div>
      ) : (
        <div className="total-line">
          <span className="note warn">BDI Serviços: configure o ISS do município para calcular</span>
          <span>—</span>
        </div>
      )}
      {resumo.bdi_materiais_pct !== null && (
        <div className="total-line">
          <span>
            BDI Materiais ({resumo.bdi_materiais_pct}%, fonte {resumo.bdi_materiais_versao})
          </span>
          <span>R$ {formatarMoeda(resumo.valor_bdi_materiais)}</span>
        </div>
      )}
      <div className="total-line grand">
        <span>TOTAL GERAL</span>
        <span>R$ {formatarMoeda(resumo.total_geral)}</span>
      </div>
      <p className="total-extenso">({valorPorExtenso(resumo.total_geral)})</p>
      <p className="note warn" style={{ marginTop: 14 }}>
        Taxas de BDI carregadas a partir da metodologia de 2023.05. A partir de 2026 a desoneração passou a 3,60% (Lei
        14.973/2024) — confirme o BDI vigente assim que for publicado o documento atualizado e reimporte via{" "}
        <code>importers/seed_reference.py</code>.
      </p>
    </div>
  );
}
